import Breadcrumbs from 'components/Breadcrumbs';
import { List } from 'immutable';
import { useObservableState } from 'observable-hooks';
import React, { useEffect, useState } from 'react';
import { IntegrationsDrive } from 'records/IntegrationsDrive';
import { IntegrationsFile } from 'records/IntegrationsFile';
import { IntegrationsFolder } from 'records/IntegrationsFolder';
import { IntegrationsFolderMetadata } from 'records/IntegrationsFolderMetadata';
import {
  IntegrationsSelectedItem,
  RawIntegrationsSelectedItem,
} from 'records/IntegrationsSelectedItem';
import { IntegrationsSite } from 'records/IntegrationsSite';
import { Subject } from 'rxjs';
import { getIntegrationSites } from 'services/integrationService';
import { getProjectDriveSourceTargetsByConnectionId } from 'services/projectDriveService';
import FolderTrie from 'utils/FolderTrie';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import useFileBrowserFolderTrie, {
  ClientActionEventMessage,
} from '../../hooks/useFileBrowserFolderTrie';
import {
  FileBrowser,
  FileBrowserItem,
  FileBrowserMessage,
} from './FileBrowser';
import { useProviderPath } from './useProviderPath';

type IntegrationsItem =
  | {
      type: 'file';
      value: IntegrationsFile;
    }
  | {
      type: 'folder';
      value: IntegrationsFolderMetadata;
    }
  | {
      type: 'drive';
      value: IntegrationsDrive;
    }
  | {
      type: 'site';
      value: IntegrationsSite;
    };

interface Props extends WithAppStoreProps {
  providerType: string;
  connectionId: string;
  manageConnection?: {
    projectDriveId?: string;
    onDiscrepancyDetected: () => void;
  };
  singleSelect?: boolean;
  filter?: string;
  onlyPdfFiles?: boolean;
  onlyFolders?: boolean;
  isExpired?: boolean;
}

const ProviderFileBrowser: React.FC<Props> = ({
  actions,
  appStore,
  providerType,
  connectionId,
  manageConnection,
  singleSelect,
  filter,
  onlyPdfFiles,
  onlyFolders,
}: Props) => {
  const { state } = appStore;
  const { integrationsCurrentFolder, integrationsDrives } = state;

  const siteRequired = providerType === 'sharepoint';

  const [loadingFolderId, setLoadingFolderId] = useState<string | undefined>();
  const [loadingDrives, setLoadingDrives] = useState<boolean>(true);
  const [loadingSavedItems, setLoadingSavedItems] = useState<boolean>(true);
  const [loadingSites, setLoadingSites] = useState<boolean>(siteRequired);
  const [currentItems, setCurrentItems] = useState<List<IntegrationsItem>>(
    List<IntegrationsItem>()
  );
  const [selections$] = useState(new Subject<ClientActionEventMessage>());
  const providerPath = useProviderPath(siteRequired);
  const { site, drive } = providerPath;

  const selectedItems$ = useFileBrowserFolderTrie(
    selections$,
    connectionId,
    providerType,
    manageConnection ? manageConnection.projectDriveId : undefined
  );

  const selectedItems = useObservableState(
    selectedItems$,
    new FolderTrie<IntegrationsSelectedItem>()
  );

  const clearLoading = () => {
    setLoadingFolderId(undefined);
    setLoadingDrives(false);
    setLoadingSites(false);
  };

  const loadSavedIds = async () => {
    if (!manageConnection) {
      setLoadingSavedItems(false);
      return;
    }

    const projectDriveSavedItems =
      await getProjectDriveSourceTargetsByConnectionId(
        state.selectedProject,
        connectionId
      );

    selections$.next({
      event: 'initial-drive-source-targets',
      targets: projectDriveSavedItems,
    });
    setLoadingSavedItems(false);
  };

  const loadIntegrationDrives = async (siteId?: string) => {
    clearLoading();
    setLoadingDrives(true);

    await actions.getIntegrationDrives(providerType, connectionId, siteId);
  };

  const loadIntegrationsSites = async () => {
    clearLoading();
    setLoadingSites(true);
    const sites = await getIntegrationSites(connectionId);
    setLoadingSites(false);
    setCurrentItems(
      sites.map((s) => ({
        type: 'site',
        value: s,
      }))
    );
  };

  useEffect(() => {
    const drives = integrationsDrives.get(connectionId);

    if (drives) {
      setCurrentItems(
        drives.map((d) => ({
          type: 'drive',
          value: d,
        }))
      );
    }
    setLoadingDrives(false);
  }, [integrationsDrives]);

  useEffect(() => {
    if (drive) {
      actions.setIntegrationsDriveId(drive.id);
    } else {
      actions.setIntegrationsDriveId('');
    }
  }, [drive]);

  useEffect(() => {
    loadSavedIds();

    if (siteRequired) {
      loadIntegrationsSites();
    } else {
      loadIntegrationDrives();
    }
  }, []);

  const getItemPathIdsFromBreadcrumbs = () => {
    return (drive ? [drive, ...providerPath.folders] : []).map(({ id }) => id);
  };

  const getFullPathIdsFromBreadcrumbs = () => {
    return site
      ? [site.id, ...getItemPathIdsFromBreadcrumbs()]
      : getItemPathIdsFromBreadcrumbs();
  };

  useEffect(() => {
    if (manageConnection) {
      const currentFolderId = List(getFullPathIdsFromBreadcrumbs()).get(-1);
      const isValid = selectedItems.validateFolder({
        folderId: currentFolderId,
        childIds: currentItems.map(({ value }) => value.id).toArray(),
      });

      if (!isValid) {
        manageConnection.onDiscrepancyDetected();
      }
    }
  }, [currentItems]);

  const setCurrentItemsFromFolder = (folder: IntegrationsFolder) => {
    const { folders, documents } = folder.items;

    const folderItems: List<IntegrationsItem> = folders.map((f) => ({
      type: 'folder',
      value: f,
    }));
    const fileItems: List<IntegrationsItem> = documents.map((d) => ({
      type: 'file',
      value: d,
    }));

    setCurrentItems(folderItems.concat(fileItems));
  };

  useEffect(() => {
    if (
      integrationsCurrentFolder.metadata.id === loadingFolderId ||
      !loadingFolderId
    ) {
      setLoadingFolderId(undefined);

      if (!loadingDrives) {
        setCurrentItemsFromFolder(integrationsCurrentFolder);
      }
    }
  }, [integrationsCurrentFolder]);

  useEffect(() => {
    const newSelectedItems = List(selectedItems.descendantLeaves());
    actions.setIntegrationsSelectedItems(newSelectedItems);
  }, [selectedItems]);

  const isAncestorSelected = getFullPathIdsFromBreadcrumbs().some(
    (pathId, index, pathIds) =>
      selectedItems.has(pathId, pathIds.slice(0, index))
  );

  const integrationsFolderSideEffect = (driveId: string, folderId?: string) => {
    if (folderId) {
      actions.setFolderSelectCurrentId(folderId);
    }

    clearLoading();
    setLoadingFolderId(folderId || driveId);

    actions.getIntegrationFolder({
      driveId,
      folderId,
      providerType,
      connectionId,
    });
  };

  const getItemSelectability = (
    name: string,
    type: 'file' | 'folder' | 'drive' | 'site'
  ) => {
    const nameEndsWithPdf = name.endsWith('.pdf');
    const isPdfFile = type === 'file' && nameEndsWithPdf;

    const isSelectableType = type === 'file' || type === 'folder';

    const isSelectableDisregardingAncestors =
      isSelectableType && (!onlyPdfFiles || isPdfFile || type === 'folder');
    const isSelectable =
      (!isAncestorSelected || singleSelect) &&
      isSelectableDisregardingAncestors &&
      !(type !== 'folder' && onlyFolders);

    const nonPdfFile = type === 'file' && !nameEndsWithPdf;

    return {
      nonPdfFile,
      isPdfFile,
      isSelectableDisregardingAncestors,
      isSelectable,
    };
  };

  const fileBrowserItems: List<FileBrowserItem> = currentItems.map((item) => {
    const { type, value } = item;

    const {
      nonPdfFile,
      isSelectable,
      isSelectableDisregardingAncestors,
      isPdfFile,
    } = getItemSelectability(value.name, type);

    const onSelect = isSelectable
      ? () => {
          if (!drive) {
            return;
          }

          if (item.type === 'file' || item.type === 'folder') {
            const isSelected = !selectedItems.has(
              value.id,
              getFullPathIdsFromBreadcrumbs()
            );

            if (isSelected) {
              const selectedItem = IntegrationsSelectedItem.deserialize({
                id: item.value.id,
                itemType: item.type,
                item: item.value,
                driveId: drive.id,
                connectionId,
                providerType,
                folderPathIds: getItemPathIdsFromBreadcrumbs(),
                siteId: providerPath.site ? providerPath.site.id : undefined,
              });

              if (singleSelect) {
                selections$.next({
                  event: 'drive-source-single-target-set',
                  target: selectedItem,
                });
              } else {
                selections$.next({
                  event: 'drive-source-targets-added',
                  targets: List([selectedItem]),
                });
              }
            } else {
              selections$.next({
                event: 'drive-source-targets-removed',
                ids: List([value.id]),
              });
            }
          }
        }
      : undefined;

    const onClick = (() => {
      if (item.type === 'site') {
        return () => {
          providerPath.selectSite(item.value.label, item.value.id);
          loadIntegrationDrives(item.value.id);
        };
      }

      if (item.type === 'drive') {
        return () => {
          integrationsFolderSideEffect(value.id);
          providerPath.selectDrive(value.name, value.id);
        };
      }
      if (!drive) {
        return undefined;
      }

      if (type === 'folder') {
        return () => {
          integrationsFolderSideEffect(drive.id, value.id);
          providerPath.enterChildFolder(value.name, value.id);
        };
      }
      if (
        isSelectable &&
        item.type === 'file' &&
        (!onlyPdfFiles || isPdfFile)
      ) {
        return () => {
          const isSelected = !selectedItems.has(
            value.id,
            getFullPathIdsFromBreadcrumbs()
          );

          if (isSelected) {
            selections$.next({
              event: 'drive-source-targets-added',
              targets: List([
                IntegrationsSelectedItem.deserialize({
                  id: item.value.id,
                  itemType: item.type,
                  item: item.value,
                  driveId: drive.id,
                  connectionId,
                  providerType,
                  folderPathIds: getItemPathIdsFromBreadcrumbs(),
                  siteId: providerPath.site ? providerPath.site.id : undefined,
                }),
              ]),
            });
          } else {
            selections$.next({
              event: 'drive-source-targets-removed',
              ids: List([value.id]),
            });
          }
        };
      }
      return undefined;
    })();

    const indeterminate =
      type === 'folder' || type === 'drive' || type === 'site'
        ? !selectedItems.isEmpty(value.id)
        : undefined;

    const isSelected =
      (isAncestorSelected && !singleSelect) ||
      selectedItems.has(value.id, getFullPathIdsFromBreadcrumbs());
    const hideCheckbox = onlyPdfFiles && nonPdfFile;
    const fadedText =
      !isSelectableDisregardingAncestors && !onClick && !singleSelect;

    return {
      isSelected,
      onSelect,
      onClick,
      type,
      value,
      indeterminate,
      hideCheckbox,
      fadedText,
    } as FileBrowserItem;
  });

  const areAllSelected =
    fileBrowserItems.filter((item) => item.isSelected).count() > 0 &&
    fileBrowserItems.every((item) => !item.onSelect || item.isSelected);

  const handleSelectAllClick =
    isAncestorSelected || singleSelect
      ? undefined
      : () => {
          const isAddAllAsSelected = !areAllSelected;
          const isRemoveAllSelected = areAllSelected;

          const itemsToChange = currentItems.reduce((toChange, currentItem) => {
            const { value, type } = currentItem;

            const { isSelectable } = getItemSelectability(value.name, type);

            if (
              isAddAllAsSelected &&
              !selectedItems.has(value.id, getFullPathIdsFromBreadcrumbs()) &&
              isSelectable
            ) {
              return toChange.push(currentItem);
            }
            if (
              isRemoveAllSelected &&
              selectedItems.has(value.id, getFullPathIdsFromBreadcrumbs())
            ) {
              return toChange.push(currentItem);
            }
            return toChange;
          }, List<IntegrationsItem>());

          const idsToRemove = itemsToChange.map((i) => i.value.id);

          if (isAddAllAsSelected) {
            if (!drive) {
              return;
            }

            selections$.next({
              event: 'drive-source-targets-added',
              targets: List(
                itemsToChange.map((item) => {
                  const { type, value } = item;
                  return IntegrationsSelectedItem.deserialize({
                    id: value.id,
                    itemType: type,
                    driveId: drive.id,
                    connectionId,
                    providerType,
                    item: value,
                    folderPathIds: getItemPathIdsFromBreadcrumbs(),
                    siteId: providerPath.site
                      ? providerPath.site.id
                      : undefined,
                  } as RawIntegrationsSelectedItem);
                })
              ),
            });
          }
          if (isRemoveAllSelected) {
            selections$.next({
              event: 'drive-source-targets-removed',
              ids: idsToRemove,
            });
          }
        };

  const onProviderClick = () => {
    actions.setFolderSelectCurrentId('');
    actions.setIntegrationsDriveId('');
    if (siteRequired) {
      loadIntegrationsSites();
    } else {
      loadIntegrationDrives();
    }
    providerPath.selectRoot();
  };

  const onSiteClick = (siteId: string) => {
    actions.setFolderSelectCurrentId('');
    actions.setIntegrationsDriveId('');
    loadIntegrationDrives(siteId);
    providerPath.selectPrevious(siteId);
  };

  const onBreadcrumbClick = (driveId: string, folderId?: string) => {
    integrationsFolderSideEffect(driveId, folderId);
    providerPath.selectPrevious(folderId || driveId);
  };

  const getFileBrowserMessage = (): FileBrowserMessage | undefined => {
    if (loadingDrives || loadingFolderId || loadingSavedItems || loadingSites) {
      return { type: 'loading', text: 'loading' };
    }
    return undefined;
  };

  return (
    <div style={{ height: 'calc(100% - 48px)' }}>
      <div className="my2">
        <Breadcrumbs
          provider={{
            type: providerType,
            onClick: onProviderClick,
          }}
          site={
            site
              ? {
                  id: site.id,
                  label: site.label,
                  onClick: () => onSiteClick(site.id),
                }
              : undefined
          }
          drive={
            providerPath.drive
              ? {
                  ...providerPath.drive,
                  onClick: () => onBreadcrumbClick(providerPath.drive!.id),
                }
              : undefined
          }
          folders={
            providerPath.drive
              ? {
                  value: providerPath.folders,
                  onClick: (breadcrumb) =>
                    onBreadcrumbClick(providerPath.drive!.id, breadcrumb.id),
                }
              : undefined
          }
        />
      </div>
      <FileBrowser
        items={fileBrowserItems}
        areAllSelected={areAllSelected}
        onSelectAllClick={handleSelectAllClick}
        message={getFileBrowserMessage()}
        filter={filter}
      />
    </div>
  );
};

export default withAppStore<Props>(ProviderFileBrowser);
